import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const STEPS = [
  {
    number: '01',
    icon: 'ri-stethoscope-line',
    title: 'Bắt Mạch Tài Sản',
    subtitle: 'Miễn phí · 60–90 phút',
    desc: 'UPLIVING phân tích hiện trạng tài sản, mô hình khai thác hiện tại và xác định chính xác tài sản đang bị "rò rỉ" ở đâu.',
    highlight: 'Miễn phí hoàn toàn',
    highlightIcon: 'ri-gift-line',
    color: 'bg-[#004D4D]',
    textColor: 'text-[#004D4D]',
    borderColor: 'border-[#004D4D]',
  },
  {
    number: '02',
    icon: 'ri-file-chart-line',
    title: 'Đề Xuất Phương Án',
    subtitle: 'Trong 48 giờ',
    desc: 'Trình bày 2–3 phương án tối ưu hóa với số liệu dự phóng dòng tiền cụ thể. Bạn chọn hướng đi phù hợp nhất với mục tiêu.',
    highlight: 'Số liệu thực tế',
    highlightIcon: 'ri-bar-chart-2-line',
    color: 'bg-[#C5A059]',
    textColor: 'text-[#C5A059]',
    borderColor: 'border-[#C5A059]',
  },
  {
    number: '03',
    icon: 'ri-hammer-line',
    title: 'Triển Khai & Thi Công',
    subtitle: '3–6 tháng',
    desc: 'Đội ngũ UPLIVING trực tiếp thiết kế, thi công và hoàn thiện theo đúng phương án đã cam kết — bạn không cần tự quản lý.',
    highlight: 'Cam kết kết quả',
    highlightIcon: 'ri-shield-check-line',
    color: 'bg-[#2D6A4F]',
    textColor: 'text-[#2D6A4F]',
    borderColor: 'border-[#2D6A4F]',
  },
  {
    number: '04',
    icon: 'ri-line-chart-line',
    title: 'Vận Hành & Tối Ưu',
    subtitle: 'Liên tục',
    desc: 'UPLIVING vận hành tài sản, báo cáo dòng tiền định kỳ và liên tục tối ưu để đảm bảo hiệu quả tối đa theo thời gian.',
    highlight: 'Báo cáo minh bạch',
    highlightIcon: 'ri-file-list-3-line',
    color: 'bg-[#8B6914]',
    textColor: 'text-[#8B6914]',
    borderColor: 'border-[#8B6914]',
  },
];

function StepCard({ step }: { step: typeof STEPS[0] }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="bg-[#F8F6F2] rounded-2xl p-6 h-full flex flex-col border border-transparent hover:border-neutral-200 transition-all duration-300 cursor-pointer group/step"
      onClick={() => setOpen(v => !v)}
    >
      {/* Number + Icon */}
      <div className="flex items-center gap-3 mb-5">
        <div className={`w-12 h-12 flex items-center justify-center ${step.color} rounded-2xl flex-shrink-0`}>
          <i className={`${step.icon} text-xl text-white`} />
        </div>
        <span className={`font-heading font-extrabold text-3xl ${step.textColor} opacity-30`}>{step.number}</span>
      </div>

      {/* Content */}
      <div className="flex-1">
        <h3 className="font-heading font-bold text-[#002626] text-base mb-1">{step.title}</h3>
        <div className="flex items-center justify-between gap-2">
          <p className={`text-xs font-heading font-semibold ${step.textColor} mb-0`}>{step.subtitle}</p>
          <i className={`ri-arrow-down-s-line text-sm flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''} md:group-hover/step:rotate-180 ${step.textColor} opacity-60`} />
        </div>
        {/* Mobile toggle */}
        <div className={`md:hidden overflow-hidden transition-all duration-300 ${open ? 'max-h-32 opacity-100 mt-3' : 'max-h-0 opacity-0 mt-0'}`}>
          <p className="text-neutral-500 text-sm font-sans leading-relaxed">{step.desc}</p>
        </div>
        {/* Desktop hover */}
        <div className="hidden md:block overflow-hidden max-h-0 opacity-0 group-hover/step:max-h-32 group-hover/step:opacity-100 group-hover/step:mt-3 transition-all duration-300">
          <p className="text-neutral-500 text-sm font-sans leading-relaxed">{step.desc}</p>
        </div>
      </div>

      {/* Highlight badge */}
      <div className={`mt-5 inline-flex items-center gap-1.5 border ${step.borderColor} rounded-full px-3 py-1.5 self-start`}>
        <i className={`${step.highlightIcon} text-xs ${step.textColor}`} />
        <span className={`text-xs font-heading font-semibold ${step.textColor}`}>{step.highlight}</span>
      </div>
    </div>
  );
}

export default function ProcessSection() {
  const navigate = useNavigate();

  return (
    <section id="process" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">

        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-[#C5A059] text-xs font-heading font-bold tracking-widest uppercase mb-4">
            QUY TRÌNH LÀM VIỆC
          </span>
          <h2 className="font-heading font-extrabold text-3xl md:text-4xl text-[#002626] leading-tight mb-4">
            4 bước từ tài sản bỏ phí<br />
            <span className="text-[#C5A059]">đến dòng tiền ổn định</span>
          </h2>
          <p className="text-neutral-500 text-base font-sans max-w-xl mx-auto leading-relaxed">
            Quy trình được chuẩn hóa qua 110+ dự án — rõ ràng, minh bạch, không phát sinh ngoài cam kết
          </p>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-14">
          {STEPS.map((step) => (
            <StepCard key={step.number} step={step} />
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="bg-gradient-to-r from-[#002626] to-[#004D4D] rounded-3xl p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-heading font-extrabold text-2xl text-white mb-2">
              Bắt đầu từ bước 1 — hoàn toàn miễn phí
            </h3>
            <p className="text-white/60 text-sm font-sans">
              Chỉ cần 60 phút Bắt Mạch, bạn sẽ biết chính xác tài sản đang bị mất bao nhiêu tiền mỗi tháng
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <button
              onClick={() => navigate('/bat-mach-tai-san')}
              className="bg-[#C5A059] hover:bg-[#D4B575] text-[#002626] font-heading font-bold text-sm px-7 py-3.5 rounded-full transition-all cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-stethoscope-line" />
              Đăng Ký Bắt Mạch Miễn Phí
            </button>
            <button
              onClick={() => navigate('/dich-vu/sua-cam-ket-ban')}
              className="bg-white/10 hover:bg-white/20 border border-white/20 text-white font-heading font-semibold text-sm px-7 py-3.5 rounded-full transition-all cursor-pointer whitespace-nowrap"
            >
              Xem Dịch Vụ
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
